/**
 * Keyboard Help Dialog
 *
 * Displays all available keyboard shortcuts organized by category.
 * Features multi-column layout and search functionality.
 * Triggered by pressing "?" anywhere in the application.
 */

import { useState, useMemo, useEffect, useRef } from 'react'
import { Search } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import {
  getShortcutsByCategory,
  getShortcutDisplay,
  CATEGORY_LABELS,
} from './shortcuts'
import type { ShortcutDefinition } from './types'

interface KeyboardHelpDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

/**
 * Keyboard shortcut badge component
 */
function ShortcutBadge({ shortcut }: { shortcut: ShortcutDefinition }) {
  const { display, label } = getShortcutDisplay(shortcut)

  return (
    <kbd
      className="inline-flex h-6 min-w-[24px] items-center justify-center rounded border border-border bg-muted px-1.5 font-mono text-xs font-medium text-muted-foreground shrink-0"
      aria-label={label}
    >
      {display}
    </kbd>
  )
}

/**
 * Single shortcut row
 */
function ShortcutRow({ shortcut }: { shortcut: ShortcutDefinition }) {
  return (
    <div className="flex items-center justify-between gap-4 py-1.5">
      <span className="text-sm text-foreground truncate">{shortcut.description}</span>
      <ShortcutBadge shortcut={shortcut} />
    </div>
  )
}

/**
 * Category section - compact for multi-column layout
 */
function CategorySection({
  category,
  shortcuts,
}: {
  category: string
  shortcuts: ShortcutDefinition[]
}) {
  if (shortcuts.length === 0) return null

  return (
    <div className="space-y-1">
      <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider pb-1 border-b border-border">
        {CATEGORY_LABELS[category] || category}
      </h3>
      <div className="space-y-0.5">
        {shortcuts.map((shortcut) => (
          <ShortcutRow key={shortcut.id} shortcut={shortcut} />
        ))}
      </div>
    </div>
  )
}

/**
 * Keyboard Help Dialog Component
 *
 * Shows all available keyboard shortcuts grouped by category.
 * Features a wider layout with multi-column display and search.
 *
 * @example
 * ```tsx
 * const [helpOpen, setHelpOpen] = useState(false)
 *
 * // In your component:
 * <KeyboardHelpDialog open={helpOpen} onOpenChange={setHelpOpen} />
 * ```
 */
export function KeyboardHelpDialog({
  open,
  onOpenChange,
}: KeyboardHelpDialogProps) {
  const [search, setSearch] = useState('')
  const searchInputRef = useRef<HTMLInputElement>(null)
  const shortcutsByCategory = getShortcutsByCategory()

  // Reset search when dialog closes
  useEffect(() => {
    if (!open) {
      setSearch('')
    } else {
      // Focus search input when dialog opens
      setTimeout(() => searchInputRef.current?.focus(), 100)
    }
  }, [open])

  // Filter shortcuts based on search query
  const filteredByCategory = useMemo(() => {
    if (!search.trim()) return shortcutsByCategory

    const query = search.toLowerCase()
    const filtered: Record<string, ShortcutDefinition[]> = {}

    for (const [category, shortcuts] of Object.entries(shortcutsByCategory)) {
      const matchingShortcuts = shortcuts.filter((shortcut) => {
        const { display } = getShortcutDisplay(shortcut)
        return (
          shortcut.description.toLowerCase().includes(query) ||
          shortcut.key.toLowerCase().includes(query) ||
          display.toLowerCase().includes(query) ||
          (CATEGORY_LABELS[category] || category).toLowerCase().includes(query)
        )
      })
      if (matchingShortcuts.length > 0) {
        filtered[category] = matchingShortcuts
      }
    }

    return filtered
  }, [shortcutsByCategory, search])

  // Split categories into columns for balanced display
  const leftCategories = ['general', 'navigation', 'actions']
  const rightCategories = ['editing', 'cms']

  const hasResults = Object.values(filteredByCategory).some(arr => arr.length > 0)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>Keyboard Shortcuts</DialogTitle>
          <DialogDescription>
            Quick actions to navigate and use the application faster.
          </DialogDescription>
        </DialogHeader>

        {/* Search Input */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            ref={searchInputRef}
            type="text"
            placeholder="Search shortcuts..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>

        {/* Multi-column layout */}
        <div className="max-h-[50vh] overflow-y-auto">
          {hasResults ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Left Column */}
              <div className="space-y-5">
                {leftCategories.map((category) => (
                  <CategorySection
                    key={category}
                    category={category}
                    shortcuts={filteredByCategory[category] || []}
                  />
                ))}
              </div>

              {/* Right Column */}
              <div className="space-y-5">
                {rightCategories.map((category) => (
                  <CategorySection
                    key={category}
                    category={category}
                    shortcuts={filteredByCategory[category] || []}
                  />
                ))}
              </div>
            </div>
          ) : (
            <div className="py-8 text-center text-muted-foreground">
              <p className="text-sm">No shortcuts found for "{search}"</p>
            </div>
          )}
        </div>

        <div className="border-t pt-3 text-center">
          <p className="text-xs text-muted-foreground">
            Press <kbd className="rounded border bg-muted px-1.5 py-0.5 font-mono text-xs">Ctrl+/</kbd> anytime to show this help
          </p>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default KeyboardHelpDialog
